import { extInfo } from 'lib/extInfo';
import { AddAlbumItemsAPICall } from 'reactApp/api/albums/AddAlbumItemsAPICall';
import { CreateAlbumAPICall } from 'reactApp/api/albums/CreateAlbumAPICall';
import { GetAlbumAPICall } from 'reactApp/api/albums/GetAlbumAPICall';
import { GetAlbumListAPICall } from 'reactApp/api/albums/GetAlbumListAPICall';
import { RemoveAlbumApiCall } from 'reactApp/api/albums/RemoveAlbumApiCall';
import { RemoveAlbumItemsAPICall } from 'reactApp/api/albums/RemoveAlbumItemsApiCall';
import { vigboDefaultTariff, vigboLinks, vigboTrialProductsMap } from 'reactApp/appHelpers/featuresHelpers/features/vigbo';
import { getUrlWithParams } from 'reactApp/appHelpers/getUrlWithParams';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { getBuilderParamsForAlbumCover } from 'reactApp/modules/albums/albums.helpers';
import type { IAlbum } from 'reactApp/modules/albums/albums.types';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import { getExtension } from 'reactApp/modules/file/helpers/getExtention';
import { getItemNameWithoutExt, getUflrStatus, normalizePublicApiInfo } from 'reactApp/modules/file/utils';
import { ESortOder } from 'reactApp/modules/sort/sort.types';
import { MalwareStatus } from 'reactApp/modules/start/start.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UrlBuilder } from 'reactApp/modules/urlBuilder/UrlBuilder';
import type { IPublicInfo, Kind } from 'reactApp/types/Tree';
import { PERIODS } from 'reactApp/utils/Period';
import { normalizeMtime } from 'reactApp/utils/tree.helpers';

import type { VigboAlbumItem } from './vigbo.types';

export interface RequestCreateVigboAlbumParams {
    title: string;
}

export const requestCreateVigboAlbum = async (params: RequestCreateVigboAlbumParams) => {
    return new CreateAlbumAPICall().makeRequest({ ...params, vigbo: true });
};

export const requestVigboAlbumsList = async () => {
    return new GetAlbumListAPICall().makeRequest({ vigbo: true });
};

export interface RequestRemoveVigboAlbumParams {
    album_id: string;
}

export const requestRemoveVigboAlbum = async (params: RequestRemoveVigboAlbumParams) => {
    return new RemoveAlbumApiCall().makeRequest(params);
};

export interface RequestVigboAlbumParams {
    album_id: string;
    order?: ESortOder;
    cursor?: string;
}

export const requestVigboAlbum = async ({ album_id, order = ESortOder.desc, cursor }: RequestVigboAlbumParams) => {
    return new GetAlbumAPICall().makeRequest({ album_id, order, cursor });
};

export interface RequestAddToVigboAlbumAlbumParams {
    album_id: string;
    paths: string[];
}

export const requestAddToVigboAlbum = async (params: RequestAddToVigboAlbumAlbumParams) => {
    return new AddAlbumItemsAPICall().makeRequest(params);
};

export interface RequestRemoveFromVigboAlbumParams {
    remove_from_cloud?: boolean;
    element_ids: string[];
    album_id: string;
}

export const requestRemoveFromVigboAlbum = async ({
    remove_from_cloud = false,
    element_ids,
    album_id,
}: RequestRemoveFromVigboAlbumParams) => {
    return new RemoveAlbumItemsAPICall().makeRequest({
        remove_from_cloud,
        element_ids,
        album_id,
    });
};

const urlBuilder = new UrlBuilder();

export const normalizeVigboAlbum = (item): IAlbum => {
    const weblinkInfo = normalizePublicApiInfo(item) as IPublicInfo;
    const thumbUrl = item.album_cover_thumb_url?.path;
    const urlBuilderParams = getBuilderParamsForAlbumCover(thumbUrl);

    return {
        ...weblinkInfo,
        cursor: item.cursor,
        id: item.id,
        kind: 'album',
        name: item.title,
        hasMoreToLoad: Boolean(item.cursor),
        parent: ROOT_FOLDER_ID,
        flags: { isMedia: true },
        isFolder: true,
        storage: EStorageType.vigbo,
        type: 'album',
        childs: item.childs || [],
        thumbnails: thumbUrl ? urlBuilder.getThumb(urlBuilderParams) : undefined,
        photosCount: item.photos_count,
        isLoaded: item.isLoaded,
        isLoading: item.isLoading,
    };
};

export const normalizeVigboAlbumItem = (item): VigboAlbumItem => {
    const ext = getExtension(item);
    const name = item.name;
    const id = item.element_id;
    const albumId = item.album_id;
    const size = item.size;
    const path = item.path || '';
    const isVirus = item?.malware?.status === MalwareStatus.infected;
    const { kind, subKind } = extInfo.get(ext);
    const uflr = getUflrStatus(item);
    const weblinkInfo = normalizePublicApiInfo(item) as IPublicInfo;

    const urlBuilderParams = path ? { ext, id: path, size, path, kind, subKind, name } : undefined;

    return {
        ...weblinkInfo,
        name,
        type: item.type,
        mtime: normalizeMtime(item.mtime || item.time),
        size,
        id,
        storage: EStorageType.vigbo,
        ext,
        nameWithoutExt: getItemNameWithoutExt(name, ext),
        parent: albumId,
        kind,
        subKind,
        isFolder: false,
        virusScan: isVirus ? 'fail' : 'pass',
        thumbnails: urlBuilderParams ? urlBuilder.getThumb(urlBuilderParams) : undefined,
        url: urlBuilderParams ? urlBuilder.getUrls(urlBuilderParams) : undefined,
        uflr,
        isInFavorites: Boolean(item?.flags?.favorite),
        home: path,
        path,
    };
};

export const getVigboUrl = (name: string, params?: Record<string, any>) => {
    return getUrlWithParams(vigboLinks, name, params);
};

export const ROOT_VIGBO_ALBUM_ID = '/';

const rootAlbum = {
    id: ROOT_VIGBO_ALBUM_ID,
    name: 'root',
    storage: EStorageType.vigbo,
    isFolder: true as const,
    kind: 'album' as Kind,
    type: 'album' as const,
    parent: '',
    childs: [],
};

/**
 * Мокаем пустой корневой vigbo-альбом
 * Выглядит как меньшее из зол, если сравнивать с добавлением его в стейт раздела
 */
export const mockVigboRootAlbum = (): IAlbum => {
    return rootAlbum;
};

export const getVigboProductIdByTrial = (tariff: BillingDataSubscriptionInfo) => {
    return vigboTrialProductsMap[tariff.productId] || vigboDefaultTariff;
};

export const getPriceByPeriod = (price: number, period: string) => {
    if (period === PERIODS.year) {
        return Math.round(price / 12);
    }

    return price;
};
